import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, CheckCircle2, ArrowLeft, Phone, Video, Users, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, isToday, isBefore } from 'date-fns';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const typeIcons = {
  discovery: <Phone className="h-5 w-5" />,
  demo: <Video className="h-5 w-5" />,
  strategy: <Users className="h-5 w-5" />,
};

export default function BookingPage() {
  const { tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1); // 1: type, 2: date/time, 3: details, 4: confirmed
  const [companyName, setCompanyName] = useState('');
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [workingHours, setWorkingHours] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [bookingError, setBookingError] = useState('');
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const [formData, setFormData] = useState({
    name: searchParams.get('name') || '',
    email: searchParams.get('email') || '',
    phone: '',
    notes: '',
  });

  const preselectedType = searchParams.get('type');
  const leadId = searchParams.get('lead_id');

  // Load booking page data
  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${API_BASE}/api/meetings/public/${tenantId}`)
      .then(r => {
        const data = r.data;
        setCompanyName(data.company_name || 'Lockene');
        setMeetingTypes(data.meeting_types || []);
        setWorkingHours(data.working_hours || null);
        if (preselectedType) {
          const found = (data.meeting_types || []).find(t => t.id === preselectedType);
          if (found) { setSelectedType(found); setStep(2); }
        }
      })
      .catch(() => {});
  }, [tenantId, preselectedType]);

  // Week days for date picker
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Load available slots when date changes
  useEffect(() => {
    if (!selectedDate || !selectedType) return;
    setSlotsLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    axios.get(`${API_BASE}/api/meetings/availability/${tenantId}?date=${dateStr}&type=${selectedType.id}`)
      .then(r => {
        setSlots(r.data.slots || []);
        setSlotsLoading(false);
      })
      .catch(() => { setSlots([]); setSlotsLoading(false); });
  }, [selectedDate, selectedType, tenantId]);

  const handleBook = async () => {
    if (!selectedSlot || !formData.name || !formData.email) return;
    setBooking(true);
    setBookingError('');
    try {
      const res = await axios.post(`${API_BASE}/api/meetings/book/${tenantId}`, {
        meeting_type_id: selectedType.id,
        start_time: selectedSlot.start,
        booker_name: formData.name,
        booker_email: formData.email,
        booker_phone: formData.phone || null,
        lead_id: leadId || null,
        notes: formData.notes || null,
      });
      setConfirmed(res.data);
      setStep(4);
    } catch (err) {
      setBookingError(
        err?.response?.data?.detail ||
        'Sorry, we could not confirm your booking. Please try another time slot or contact us directly.'
      );
    } finally {
      setBooking(false);
    }
  };

  const isDayEnabled = (date) => {
    if (isBefore(date, new Date()) && !isToday(date)) return false;
    if (!workingHours?.days) return true;
    const day = format(date, 'EEEE').toLowerCase();
    return workingHours.days[day]?.enabled !== false;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="booking-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Calendar className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold" data-testid="booking-company-name">{companyName}</h1>
          <p className="text-muted-foreground mt-1">Schedule a meeting with our team</p>
        </div>

        {/* Step 1: Select Meeting Type */}
        {step === 1 && (
          <div className="space-y-3" data-testid="step-select-type">
            <p className="text-sm font-medium text-muted-foreground mb-4">Select a meeting type</p>
            {meetingTypes.map(t => (
              <Card
                key={t.id}
                className="cursor-pointer transition-all hover:ring-2 hover:ring-primary/20 hover:shadow-md"
                onClick={() => { setSelectedType(t); setStep(2); }}
                data-testid={`booking-type-${t.id}`}
              >
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${t.color}15`, color: t.color }}>
                      {typeIcons[t.id] || <Calendar className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{t.name}</h3>
                      <p className="text-sm text-muted-foreground">{t.description}</p>
                    </div>
                    <Badge variant="outline">
                      <Clock className="h-3 w-3 mr-1" /> {t.duration_minutes} min
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && selectedType && (
          <Card data-testid="step-select-datetime">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setStep(1); setSelectedDate(null); setSelectedSlot(null); }}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle className="text-lg">{selectedType.name}</CardTitle>
                  <CardDescription>{selectedType.duration_minutes} minutes</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Week Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="sm" onClick={() => setWeekStart(d => addDays(d, -7))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setWeekStart(d => addDays(d, 7))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Day Selector */}
              <div className="grid grid-cols-7 gap-2 mb-6">
                {weekDays.map(day => {
                  const enabled = isDayEnabled(day);
                  const selected = selectedDate && isSameDay(day, selectedDate);
                  return (
                    <button
                      key={day.toISOString()}
                      disabled={!enabled}
                      onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                      className={`p-3 rounded-lg text-center transition-all ${
                        selected ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' :
                        enabled ? 'hover:bg-muted cursor-pointer' : 'opacity-30 cursor-not-allowed'
                      }`}
                      data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <span className="text-xs block">{format(day, 'EEE')}</span>
                      <span className="text-lg font-semibold block">{format(day, 'd')}</span>
                    </button>
                  );
                })}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <p className="text-sm font-medium mb-3">
                    Available times for {format(selectedDate, 'EEEE, MMMM d')}
                  </p>
                  {slotsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No available slots for this day</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots-grid">
                      {slots.map(slot => (
                        <button
                          key={slot.start}
                          onClick={() => setSelectedSlot(slot)}
                          className={`py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${
                            selectedSlot?.start === slot.start
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-border hover:border-primary/40 hover:bg-primary/5'
                          }`}
                          data-testid={`slot-${slot.display.replace(/\s/g, '-')}`}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedSlot && (
                    <Button className="w-full mt-6" onClick={() => setStep(3)} data-testid="continue-to-details-btn">
                      Continue
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Enter Details */}
        {step === 3 && (
          <Card data-testid="step-enter-details">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle className="text-lg">Enter your details</CardTitle>
                  <CardDescription>
                    {selectedType.name} &middot; {format(new Date(selectedSlot.start), 'MMM d, yyyy h:mm a')} &middot; {selectedType.duration_minutes} min
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com"
                  data-testid="booking-email-input"
                />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                  data-testid="booking-phone-input"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any specific topics you'd like to discuss?"
                  rows={3}
                  data-testid="booking-notes-input"
                />
              </div>
              {bookingError && (
                <p className="text-sm text-red-500 mt-3" data-testid="booking-error">{bookingError}</p>
              )}
              <Button
                className="w-full"
                disabled={!formData.name || !formData.email || booking}
                onClick={handleBook}
                data-testid="confirm-booking-btn"
              >
                {booking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {booking ? 'Booking...' : 'Confirm Booking'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && confirmed && (
          <Card data-testid="step-confirmed">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Meeting Confirmed!</h2>
              <p className="text-muted-foreground mt-2">
                You're all set. A confirmation email has been sent to {formData.email}.
              </p>
              <div className="mt-6 bg-muted/50 rounded-lg p-4 text-left max-w-sm mx-auto space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Meeting</span>
                  <span className="text-sm font-medium">{confirmed.meeting_type_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Date</span>
                  <span className="text-sm font-medium">
                    {new Date(confirmed.start_time).toLocaleDateString('en-US', { timeZone: workingHours?.timezone || undefined, month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Time</span>
                  <span className="text-sm font-medium">
                    {new Date(confirmed.start_time).toLocaleTimeString('en-US', { timeZone: workingHours?.timezone || undefined, hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Duration</span>
                  <span className="text-sm font-medium">{confirmed.duration_minutes} minutes</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Powered by Lockene CRM
        </p>
      </div>
    </div>
  );
}
